/**
 * Firma de URLs de archivos privados con caché y agrupación en lotes.
 *
 * Un reporte con muchas fotos pedía una firma por imagen (una petición HTTP cada una) y
 * agotaba el rate limit. Aquí:
 *   - las peticiones que llegan en la misma ventana (BATCH_WINDOW_MS) se mandan juntas
 *     a POST /files/sign-batch (en trozos de MAX_BATCH);
 *   - la URL firmada se cachea en memoria hasta poco antes de que expire su token;
 *   - si dos componentes piden el mismo path a la vez, comparten la misma petición.
 */

import { apiClient } from './apiClient'

export type SignedUrl = {
  token: string
  url: string
  absolute_url: string | null
  expires_in: number
}

type BatchResponse = {
  signed: Record<string, SignedUrl>
  failed: Record<string, string>
}

type Pending = {
  resolve: (signed: SignedUrl) => void
  reject: (err: Error) => void
}

/** Debe coincidir con MAX_BATCH de FilesController.php. */
const MAX_BATCH = 50
const BATCH_WINDOW_MS = 20
/** Se descarta la URL cacheada este tiempo antes de expirar, para no servir una a punto de caducar. */
const EXPIRY_MARGIN_MS = 60_000

const cache = new Map<string, { signed: SignedUrl; expiresAt: number }>()
const inFlight = new Map<string, Promise<SignedUrl>>()
const queue = new Map<string, Pending[]>()
let timer: ReturnType<typeof setTimeout> | null = null

async function sendBatch(paths: string[]): Promise<void> {
  let res: BatchResponse | null = null
  let failure: Error | null = null
  try {
    res = await apiClient.post<BatchResponse>('/files/sign-batch', { paths })
  } catch (err) {
    failure = err instanceof Error ? err : new Error('No se pudo firmar los archivos.')
  }

  for (const path of paths) {
    const waiters = queue.get(path) ?? []
    queue.delete(path)
    const signed = res?.signed[path]
    if (signed) {
      cache.set(path, { signed, expiresAt: Date.now() + signed.expires_in * 1000 - EXPIRY_MARGIN_MS })
      waiters.forEach((w) => w.resolve(signed))
    } else {
      const err = failure ?? new Error(res?.failed[path] ?? 'file_not_signed')
      waiters.forEach((w) => w.reject(err))
    }
  }
}

function flush(): void {
  timer = null
  const paths = [...queue.keys()]
  for (let i = 0; i < paths.length; i += MAX_BATCH) {
    void sendBatch(paths.slice(i, i + MAX_BATCH))
  }
}

export function signFile(path: string): Promise<SignedUrl> {
  const hit = cache.get(path)
  if (hit && hit.expiresAt > Date.now()) return Promise.resolve(hit.signed)

  const existing = inFlight.get(path)
  if (existing) return existing

  const promise = new Promise<SignedUrl>((resolve, reject) => {
    const waiters = queue.get(path) ?? []
    waiters.push({ resolve, reject })
    queue.set(path, waiters)
    if (timer === null) timer = setTimeout(flush, BATCH_WINDOW_MS)
  }).finally(() => inFlight.delete(path))

  inFlight.set(path, promise)
  return promise
}

/** Solo para tests. */
export function resetFileSignerForTests(): void {
  cache.clear()
  inFlight.clear()
  queue.clear()
  if (timer !== null) clearTimeout(timer)
  timer = null
}
